import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Observable, Subject, ReplaySubject, interval } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { environment } from '@environments/environment';
import { SessionState } from '../models/session.interface';
import { Platform } from '@ionic/angular/standalone';

const STORE_KEY = 'lastSessionAction';

@Injectable({
  providedIn: 'root'
})
export class SessionService implements OnDestroy {
  private readonly SESSION_DURATION = environment.session.duration;
  private readonly WARNING_TIME = environment.session.warningTime;
  private readonly CHECK_INTERVAL = 1000;
  private destroy$ = new Subject<void>();
  private sessionState = new ReplaySubject<SessionState>(1);
  private sessionEndedSubject = new Subject<void>();
  private eventListeners: Array<() => void> = [];
  private currentState: SessionState = { expirationTime: 0, remainingTime: 0, isWarning: false };
  sessionEnded$ = this.sessionEndedSubject.asObservable();
  
  private preLogoutTasks: Array<() => Promise<any>> = [];

  constructor(
    private auth: Auth,
    private router: Router,
    private logger: LoggerService,
    private ngZone: NgZone,
    private platform: Platform
  ) {
    this.checkSessionOnPageLoad();
    this.initVisibilityChangeListener();
  }
  
  public addPreLogoutTask(task: () => Promise<any>): void {
    this.preLogoutTasks.push(task);
  }

  private initVisibilityChangeListener(): void {
    if (this.platform.is('mobile')) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible') {
          this.ngZone.run(() => {
            this.checkSessionOnVisibilityChange();
          });
        }
      };
      document.addEventListener('visibilitychange', visibilityChangeHandler);
      this.destroy$.subscribe(() => {
        document.removeEventListener('visibilitychange', visibilityChangeHandler);
      });
    });
  }

  private checkSessionOnVisibilityChange(): void {
    if (!this.auth.currentUser) {
      return;
    }
    const lastAction = this.getLastAction();
    const now = Date.now();
    if (lastAction && lastAction + this.SESSION_DURATION < now) {
      this.logger.addLogItem('SESSION_EXPIRED', 'Session expired on tab focus', 'info');
      this.endSession();
    }
  }

  private checkSessionOnPageLoad(): void {
    if (this.platform.is('mobile')) {
      return;
    }
    if (this.auth.currentUser) {
      const lastAction = this.getLastAction();
      const now = Date.now();
      if (!lastAction || lastAction + this.SESSION_DURATION < now) {
        this.logger.addLogItem('SESSION_EXPIRED', 'Session expired during absence', 'info');
        this.endSession();
      } else {
        this.updateSessionState(lastAction + this.SESSION_DURATION);
        this.startTimer();
        this.initListeners();
      }
    }
  }

  public initListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const clickHandler = () => this.resetActivity();
      document.body.addEventListener('click', clickHandler);
      this.eventListeners.push(() => document.body.removeEventListener('click', clickHandler));
      const keydownHandler = () => this.resetActivity();
      document.body.addEventListener('keydown', keydownHandler);
      this.eventListeners.push(() => document.body.removeEventListener('keydown', keydownHandler));
      const touchstartHandler = () => this.resetActivity();
      document.body.addEventListener('touchstart', touchstartHandler);
      this.eventListeners.push(() => document.body.removeEventListener('touchstart', touchstartHandler));
    });
  }

  public removeListeners(): void {
    this.eventListeners.forEach(removeListener => removeListener());
    this.eventListeners = [];
  }

  private getLastAction(): number | null {
    const value = localStorage.getItem(STORE_KEY);
    return value ? parseInt(value, 10) : null;
  }

  private setLastAction(time: number): void {
    localStorage.setItem(STORE_KEY, time.toString());
  }

  private resetActivity(): void {
    this.ngZone.run(() => {
      const now = Date.now();
      this.setLastAction(now);
      const expirationTime = now + this.SESSION_DURATION;
      this.updateSessionState(expirationTime);
    });
  }

  private updateSessionState(expirationTime: number): void {
    const now = Date.now();
    const remainingTime = Math.max(0, expirationTime - now);
    const isWarning = remainingTime <= this.WARNING_TIME;
    this.currentState = { expirationTime, remainingTime, isWarning };
    this.sessionState.next(this.currentState);
  }

  private startTimer(): void {
    this.ngZone.runOutsideAngular(() => {
      interval(this.CHECK_INTERVAL).pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.ngZone.run(() => {
          const lastAction = this.getLastAction();
          if (lastAction) {
            const expirationTime = lastAction + this.SESSION_DURATION;
            this.updateSessionState(expirationTime);
            if (this.currentState.remainingTime === 0) {
              this.endSession();
            }
          }
        });
      });
    });
  }

  async startSession(): Promise<void> {
    if (this.platform.is('mobile')) {
      return;
    }
    try {
      const user = this.auth.currentUser;
      if (!user) {
        return;
      }
      const lastAction = this.getLastAction();
      const now = Date.now();
      if (lastAction && (lastAction + this.SESSION_DURATION < now)) {
        this.logger.addLogItem('SESSION_EXPIRED', 'Session expired during absence', 'info');
        await this.endSession();
        return;
      }
      this.setLastAction(now);
      this.updateSessionState(now + this.SESSION_DURATION);
      this.startTimer();
      this.initListeners();
      await user.getIdToken(true);
    } catch (error) {
      console.error('[SessionDebug] Session start error:', error);
      this.logger.addLogItem('SESSION_START_ERROR', JSON.stringify(error), 'error');
    }
  }

  async refreshSession(): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        return;
      }
      const lastAction = this.getLastAction();
      const now = Date.now();

      if (!lastAction || lastAction + this.SESSION_DURATION < now) {
        this.logger.addLogItem('SESSION_EXPIRED', 'Cannot refresh expired session', 'info');
        await this.endSession();
        return;
      }

      await user.getIdToken(true);
      this.resetActivity();
    } catch (error) {
      console.error('[SessionDebug] Session refresh error:', error);
      this.logger.addLogItem('SESSION_REFRESH_ERROR', JSON.stringify(error), 'error');
    }
  }

  async endSession(): Promise<void> {
    try {
      await Promise.all(this.preLogoutTasks.map(task => task()));
      this.sessionEndedSubject.next();
      this.destroy$.next();
      this.removeListeners();
      localStorage.removeItem(STORE_KEY);
      await this.auth.signOut();
      this.router.navigate(['/']);
    } catch (error) {
      console.error('[SessionDebug] Session end error:', error);
      this.logger.addLogItem('SESSION_END_ERROR', JSON.stringify(error), 'error');
    }
  }

  getRemainingTime(): Observable<string> {
    return this.sessionState.pipe(
      map(state => {
        const minutes = Math.floor(state.remainingTime / 60000);
        const seconds = Math.floor((state.remainingTime % 60000) / 1000);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
      })
    );
  }

  shouldWarnUser(): Observable<boolean> {
    return this.sessionState.pipe(
      map(state => state.isWarning && state.remainingTime > 0)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeListeners();
  }
}
